import { components } from "@/api-schema";
import { useCallback, useMemo, useState } from "react";
import {
  createGroupMembershipSelectionState,
  mergeUsersByUuid,
  syncGroupMembershipSelectionState,
} from "./_helpers/group-membership-selection.helpers";
import {
  applyUserSelectionChange,
  isInitialMembershipUser,
  resolveSelectedUsers,
} from "./_helpers/group-membership-selection-update.helpers";

type UserWithMembership = components["schemas"]["UserWithMembershipResponseDto"];

interface GroupMembershipSelection {
  allSelectedUsers: UserWithMembership[];
  currentPageSelectedUsers: UserWithMembership[];
  userUuidsToAdd: string[];
  userUuidsToRemove: string[];
  handleSelectionChange: (selected: UserWithMembership[] | string[]) => void;
  handleClearSelection: () => void;
  handleRowClick: (clickedUser: UserWithMembership) => void;
}

/**
 * Manages cross-page membership selection state and derives the delta (userUuidsToAdd/userUuidsToRemove)
 * needed by the sync mutation payload.
 *
 * When a page loads, rows with hasMembership=true are recorded as the baseline.
 * Explicit local deselections always win over later page/search reloads.
 */
export function useGroupMembershipSelection(
  currentPageData: UserWithMembership[] | undefined,
): GroupMembershipSelection {
  const [selectionState, setSelectionState] = useState(createGroupMembershipSelectionState);
  const nextSelectionState = syncGroupMembershipSelectionState(selectionState, currentPageData);

  if (nextSelectionState !== selectionState) {
    setSelectionState(nextSelectionState);
  }

  const { addedUsers, discoveredInitialUsers, removedInitialUserUuids } = selectionState;

  const initialMemberUuids = useMemo(() => discoveredInitialUsers.map((user) => user.uuid), [discoveredInitialUsers]);

  const initialMemberUuidSet = useMemo(() => new Set(initialMemberUuids), [initialMemberUuids]);

  const removedInitialUserUuidSet = useMemo(() => new Set(removedInitialUserUuids), [removedInitialUserUuids]);

  const allSelectedUsers = useMemo(
    () =>
      mergeUsersByUuid(
        discoveredInitialUsers.filter((user) => !removedInitialUserUuidSet.has(user.uuid)),
        addedUsers,
      ),
    [addedUsers, discoveredInitialUsers, removedInitialUserUuidSet],
  );

  const allSelectedUserUuidSet = useMemo(() => new Set(allSelectedUsers.map((user) => user.uuid)), [allSelectedUsers]);

  const currentPageSelectedUsers = useMemo(
    () => currentPageData?.filter((user) => allSelectedUserUuidSet.has(user.uuid)) ?? [],
    [allSelectedUserUuidSet, currentPageData],
  );

  const userUuidsToAdd = useMemo(
    () => addedUsers.filter((user) => !initialMemberUuidSet.has(user.uuid)).map((user) => user.uuid),
    [addedUsers, initialMemberUuidSet],
  );

  const userUuidsToRemove = useMemo(
    () => initialMemberUuids.filter((memberUuid) => removedInitialUserUuidSet.has(memberUuid)),
    [initialMemberUuids, removedInitialUserUuidSet],
  );

  const handleSelectionChange = useCallback(
    (selected: UserWithMembership[] | string[]) => {
      const currentPageUsers = currentPageData ?? [];
      const selectedUsers = resolveSelectedUsers(selected, currentPageUsers);
      const selectedUserUuids = new Set(selectedUsers.map((user) => user.uuid));
      const currentPageUserUuids = new Set(currentPageUsers.map((user) => user.uuid));

      setSelectionState((previousState) => {
        let nextState = previousState;

        for (const user of currentPageUsers) {
          nextState = applyUserSelectionChange(nextState, user, selectedUserUuids.has(user.uuid), initialMemberUuidSet);
        }

        const selectedUsersOutsideCurrentPage = selectedUsers.filter(
          (user) => !currentPageUserUuids.has(user.uuid) && !isInitialMembershipUser(user, initialMemberUuidSet),
        );

        if (selectedUsersOutsideCurrentPage.length === 0) {
          return nextState;
        }

        return {
          ...nextState,
          addedUsers: mergeUsersByUuid(nextState.addedUsers, selectedUsersOutsideCurrentPage),
        };
      });
    },
    [currentPageData, initialMemberUuidSet],
  );

  const handleClearSelection = useCallback(() => {
    setSelectionState((previousState) => ({
      ...previousState,
      addedUsers: [],
      removedInitialUserUuids: [...initialMemberUuids],
      // Keep clear-all authoritative for later pages until the user saves or leaves the screen.
      isInitialAutoSelectionSuspended: true,
    }));
  }, [initialMemberUuids]);

  const handleRowClick = useCallback(
    (clickedUser: UserWithMembership) => {
      const isSelected = allSelectedUserUuidSet.has(clickedUser.uuid);

      setSelectionState((previousState) =>
        applyUserSelectionChange(previousState, clickedUser, !isSelected, initialMemberUuidSet),
      );
    },
    [allSelectedUserUuidSet, initialMemberUuidSet],
  );

  return {
    allSelectedUsers,
    currentPageSelectedUsers,
    userUuidsToAdd,
    userUuidsToRemove,
    handleSelectionChange,
    handleClearSelection,
    handleRowClick,
  };
}
